import { useState, useEffect, useMemo } from 'react';
import { Shield, UserMinus, Mail, MapPin, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { getGlobalAdmins, updateUserPlatformRole } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import LoadingSpinner from '../layout/LoadingSpinner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

type Admin = {
  id: string;
  email: string | null;
  username: string | null;
  fullName: string | null;
  platformRole: string;
  createdAt: Date;
  primaryLocationId: string | null;
  primaryLocation?: {
    id: string;
    name: string;
    slug: string | null;
  } | null;
};

type SortField = 'user' | 'email' | 'location' | 'since';
type SortDir = 'asc' | 'desc';

const GlobalAdminsTab = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const data = await getGlobalAdmins();
      setAdmins(data as Admin[]);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load global admins');
    } finally {
      setLoading(false);
    }
  };

  const sortedAdmins = useMemo(() => {
    if (!sortField) return admins;

    return [...admins].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (sortField) {
        case 'user':
          aVal = (a.fullName || a.username || '').toLowerCase();
          bVal = (b.fullName || b.username || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'location':
          aVal = (a.primaryLocation?.name || '').toLowerCase();
          bVal = (b.primaryLocation?.name || '').toLowerCase();
          break;
        case 'since':
          aVal = new Date(a.createdAt).toISOString();
          bVal = new Date(b.createdAt).toISOString();
          break;
      }

      const result = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? result : -result;
    });
  }, [admins, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const handleRemoveAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setShowRemoveDialog(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      setRemovingId(selectedAdmin.id);
      await updateUserPlatformRole({
        userId: selectedAdmin.id,
        platformRole: 'USER'
      });

      toast.success(`Admin privileges removed from ${selectedAdmin.email || selectedAdmin.username}`);
      setShowRemoveDialog(false);
      setSelectedAdmin(null);
      await loadAdmins();
    } catch (error: any) {
      if (error.message.includes('cannot remove your own admin status')) {
        toast.error('You cannot remove your own admin privileges');
      } else {
        toast.error(error.message || 'Failed to remove admin privileges');
      }
    } finally {
      setRemovingId(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full">
      {/* Column Headers */}
      <div className="bg-sidebar-hover border-b border-border">
        <div className="px-6 py-3 grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider text-content-text-muted select-none">
          <div
            className="col-span-3 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('user')}
          >
            User <SortIcon field="user" />
          </div>
          <div
            className="col-span-3 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('email')}
          >
            Email <SortIcon field="email" />
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('location')}
          >
            Primary Location <SortIcon field="location" />
          </div>
          <div
            className="col-span-2 flex items-center cursor-pointer hover:text-content-text transition-colors"
            onClick={() => handleSort('since')}
          >
            Member Since <SortIcon field="since" />
          </div>
          <div className="col-span-2 text-right">
            Actions
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="w-full">
        {sortedAdmins.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg font-medium">No global admins found</p>
            <p className="text-sm mt-2">No administrators configured</p>
          </div>
        )}

        {sortedAdmins.map((admin) => (
          <div
            key={admin.id}
            className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700"
          >
            <div className="col-span-3">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {admin.fullName || admin.username || 'Unknown User'}
                </span>
                {admin.username && admin.fullName && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">@{admin.username}</span>
                )}
              </div>
            </div>
            <div className="col-span-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">{admin.email || 'No email'}</span>
              </div>
            </div>
            <div className="col-span-2">
              {admin.primaryLocation ? (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">{admin.primaryLocation.name}</span>
                </div>
              ) : (
                <span className="text-sm text-gray-500">No location</span>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {formatDate(admin.createdAt)}
              </span>
            </div>
            <div className="col-span-2 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemoveAdmin(admin)}
                disabled={removingId === admin.id}
                className="text-destructive hover:text-destructive"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="px-6 py-6">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Managing Administrators</h4>
          <p className="text-sm text-muted-foreground mb-3">
            To promote a user to Administrator or manage roles:
          </p>
          <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
            <li>Navigate to <strong className="text-foreground">Admin → User Roles</strong></li>
            <li>Find the user and assign the desired role</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-3">
            <strong className="text-foreground">Note:</strong> You cannot remove your own admin privileges for security reasons. Platform Owners cannot be demoted through this interface.
          </p>
        </div>
      </div>

      {/* Remove Admin Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Admin Privileges?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove admin privileges from{' '}
              <strong>{selectedAdmin?.email || selectedAdmin?.username}</strong>?
              <br />
              <br />
              This user will no longer have access to admin features and settings. This action can be reversed by granting admin privileges again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveAdmin}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GlobalAdminsTab;



